/**
 * Attachment Hooks
 *
 * Provides hooks for the generic attachment system with
 * presigned URL upload flow and multi-entity queries.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { Attachment, AttachmentEntityType } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface EntityRef {
  entityType: AttachmentEntityType;
  entityId: string;
  workOrderNumber?: string | null;
}

interface UploadUrlResponse {
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
  expiresIn: number;
}

interface RegisterAttachmentInput {
  fileKey: string;
  fileUrl: string;
  originalFileName: string;
  mimeType: string;
  fileSizeBytes?: number;
  tags?: string[];
  description?: string;
  entityLinks: EntityRef[];
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch attachments for multiple entities (e.g., booking + vehicle)
 */
export function useMultiEntityAttachments(entities: EntityRef[]) {
  const entitiesParam = JSON.stringify(entities);

  return useQuery({
    queryKey: queryKeys.attachments.byEntities(entities),
    queryFn: async () => {
      const result = await api.get<{ data: Attachment[] }>(
        `/attachments/by-entities?entities=${encodeURIComponent(entitiesParam)}`
      );
      return result.data;
    },
    enabled: entities.length > 0,
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Request a presigned upload URL
 */
export function useRequestUploadUrl() {
  return useMutation({
    mutationFn: (input: {
      mimeType: string;
      fileName: string;
      fileSizeBytes?: number;
      entityLinks: EntityRef[];
    }) => api.post<UploadUrlResponse>('/attachments/upload-url', input),
  });
}

/**
 * Register a completed upload
 */
export function useRegisterAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RegisterAttachmentInput) =>
      api.post<Attachment>('/attachments', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.attachments.all });
    },
  });
}

/**
 * Update attachment tags/description
 */
export function useUpdateAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      tags?: string[];
      description?: string | null;
    }) => api.patch<Attachment>(`/attachments/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.attachments.all });
    },
  });
}

/**
 * Delete an attachment
 */
export function useDeleteAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/attachments/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.attachments.all });
    },
  });
}

/**
 * Upload a file using the presigned URL flow:
 * 1. Request presigned URL from API
 * 2. PUT file directly to S3
 * 3. Register the attachment in the API
 */
export async function uploadFile(
  file: File,
  entityLinks: EntityRef[],
  tags: string[],
  description: string,
  requestUploadUrl: (input: {
    mimeType: string;
    fileName: string;
    fileSizeBytes?: number;
    entityLinks: EntityRef[];
  }) => Promise<UploadUrlResponse>,
  registerAttachment: (input: RegisterAttachmentInput) => Promise<Attachment>,
  onProgress?: (progress: number) => void
): Promise<Attachment> {
  // 1. Get presigned URL
  onProgress?.(10);
  // Declared size becomes a signed Content-Length on the presigned URL,
  // so S3 enforces the exact byte count of this upload.
  const { uploadUrl, fileKey, fileUrl } = await requestUploadUrl({
    mimeType: file.type,
    fileName: file.name,
    fileSizeBytes: file.size,
    entityLinks,
  });

  // 2. Upload directly to S3
  onProgress?.(30);
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.statusText}`);
  }

  onProgress?.(70);

  // 3. Register in API
  const attachment = await registerAttachment({
    fileKey,
    fileUrl,
    originalFileName: file.name,
    mimeType: file.type,
    fileSizeBytes: file.size,
    tags,
    description,
    entityLinks,
  });

  onProgress?.(100);
  return attachment;
}
